import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { DocumentType } from 'src/app/core/enums/document-type.enum';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { OnboardingModuleStatuses } from 'src/app/core/enums/onboarding-module-status.enum';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IDocumentType } from 'src/app/core/interfaces/common/document/document-type.interface';
import { IDocument } from 'src/app/core/interfaces/common/document/document.interface';
import { IFinancialProfile } from 'src/app/core/interfaces/onboarding/financial-profile/financial-profile.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageContent } from 'src/app/core/models/common/page-content.model';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { ContentService } from 'src/app/core/services/common/content.service';
import { DocumentService } from 'src/app/core/services/common/document.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { FinancialProfileService } from 'src/app/core/services/onboarding/finacial-profile.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { selectFinancialProfileStatusId } from 'src/app/core/store/onboarding/reducers+selectors/enrollment.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { PageStateService } from '../../../../core/services/onboarding/page-state.service';
import { ContentIDConstants } from '../content-id.constants';

@Component({
  selector: 'app-ar-statements',
  templateUrl: './ar-statements.component.html',
  styleUrls: ['./ar-statements.component.scss'],
})
export class ArStatementsComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  homeState: PageState;
  backState: PageState;
  skipState: PageState;
  nextState: PageState;
  questionText = 'Upload Document';
  tooltipContent: IPageContent;
  documentType: IDocumentType;
  subscriptions: Subscription[] = [];
  uploadSaveUrl = '';
  payload: IFinancialProfile;
  pageStateID: string;
  form: FormGroup;
  documentBehaviorSubject = new BehaviorSubject<IDocument[]>([]);

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private cs: ContentService,
    private ees: EnrollmentExtendedService,
    private fp: FinancialProfileService,
    private ds: DocumentService,
    private formBuilder: FormBuilder,
    private store: Store<IAppState>
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.populateDocumnetType(data['documentType']);
        let moduleStatus = OnboardingModuleStatuses.ACTIVE;
        const fpStatusId$ = this.store.select(selectFinancialProfileStatusId);
        this.subscriptions.push(
          fpStatusId$.subscribe((fpStatusId) => {
            moduleStatus = fpStatusId;
          })
        );

        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.pageStateID = params['stateId'];
            if (moduleStatus === OnboardingModuleStatuses.ACTIVE) {
              this.ees.updateFinancialProfileStateId(
                this.pageStateID,
                this.homeState.id
              );
            } else {
              this.ees.updateOnlyFinancialProfileStateId(this.pageStateID);
            }
          })
        );
        this.documentBehaviorSubject.next(
          await this.fp.getDocumentByDocTypeId(this.documentType.documentTypeId)
        );
      })
    );

    this.form = this.formBuilder.group({
      filesData: [[], Validators.required],
    });

    this.payload = this.fp.getPayload();
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  populatePageStates(transitions: ITransition[]): void {
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.question)?.displayText ??
      this.questionText;
    this.tooltipContent =
      contentDict.get(ContentIDConstants.tooltip) ?? new PageContent();
  }

  populateDocumnetType(documentType: IDocumentType[]) {
    documentType
      .filter(
        (documentType) => documentType.documentTypeName === DocumentType.ARAging
      )
      .map((row) => (this.documentType = row));
  }

  private async updateStatesAndContent(stateId: string) {
    const transitions = await this.ps.getPageTransitionsByStateId(
      stateId.toString()
    );
    const contentDict = await this.cs.getContentDictByStateId(stateId);
    this.populatePageStates(transitions);
    this.populatePageContent(contentDict);
  }

  async onDelete(docId: number) {
    const result = this.ds.deleteDocumentById(docId);
    this.documentBehaviorSubject.next(
      await this.fp.getDocumentByDocTypeId(this.documentType.documentTypeId)
    );
    return result;
  }

  async onDownload(docId: number): Promise<IDocument> {
    return this.ds.downloadEnrollmentDocument(docId);
  }

  async submit() {
    const localFiles = this.form.get('filesData')?.value;

    if (
      localFiles.length === 0 &&
      this.documentBehaviorSubject.getValue().length === 0 &&
      this.skipState.disabled === true
    ) {
      return false;
    }

    if (
      localFiles.length === 0 &&
      (this.documentBehaviorSubject.getValue().length > 0 ||
        this.skipState.disabled === false)
    ) {
      await this.fp.completeArAging();
      return true;
    }

    if (localFiles.length > 0) {
      this.payload.documentTypeId = this.documentType.documentTypeId;
      this.payload.pageStateId = this.pageStateID;
      this.payload.needToSaveInvoicePerMonth = false;
      this.payload.needToSaveInvoiceTerm = false;
      this.payload.needToUploadBankStatement = false;

      for (let i = 0; i < localFiles.length; i++) {
        this.payload.file.push(localFiles[i].rawFile);
      }

      await Promise.allSettled([
        this.fp.saveFinancialProfile(this.payload),
        this.fp.completeArAging(),
      ]);
      return true;
    }
    // mark all controls as touched to trigger validation
    this.form.markAllAsTouched();
    return false;
  }

  async skip() {
    await this.fp.completeArAging();
    return true;
  }
}
